import { computed, inject, linkedSignal, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ValidationErrors } from '@angular/forms';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { catchError, map, of } from 'rxjs';
import { GenericValue } from '../../../../core/model/generic-value';
import { HisUiService } from '../../../../core/service/base/his-ui.service';
import { HisUploadConfiguration } from '../../../model/his-upload-configuration';
import { HisDocumentMetadataCategoryService } from '../../../service/his-document-metadata-category.service';
import { HisSelectFile } from '../model/his-select-file.model';

/** i18n key of the message reported while a selected file has no category. */
const TRANSLATION_CATEGORY_REQUIRED = 'cs.sys.hisng.upload.documentMetadataCategory.requiredMessage';

/**
 * The state {@link HisSelectFileCategoryCoordinator} works on, provided by the component
 * that owns the selection.
 */
export interface HisSelectFileCategorySelection {

    /** The currently selected files. */
    readonly files: Signal<HisSelectFile[]>;

    /** The configuration of the upload field; {@code null} while it is still unknown. */
    readonly configuration: Signal<HisUploadConfiguration | null>;

    /** `true` while the selection may not be changed at all, e.g. disabled or readonly. */
    readonly locked: Signal<boolean>;

    /**
     * Hands the selection back to its owner, carrying the category of every file as the
     * coordinator determined it. Only called when at least one file actually changed.
     */
    readonly apply: ( files: HisSelectFile[] ) => void;
}

/**
 * The document-metadata category of a file selection.
 *
 * All selected files of a {@code <his-select-file />} share a single category, which is
 * partly chosen by the user and partly derived from what is configured and from what the
 * files already carry. Determining, presenting, applying and validating that category is
 * an own concern with a fair amount of rules, which is why it lives here instead of in the
 * component.
 *
 * Create this in an injection context, i.e. as a field of the owning component.
 */
export class HisSelectFileCategoryCoordinator {

    private readonly categoryService = inject( HisDocumentMetadataCategoryService );
    private readonly hisUiService = inject( HisUiService );

    /** All categories known to the server, used to resolve ids to labels. */
    readonly all = toSignal(
        this.categoryService.getDocumentMetadataCategories().pipe(
            map( ( categories ) => categories ?? [] ),
            catchError( ( error ) => {
                console.error( 'his-select-file: could not load the document metadata categories', error );
                return of( [] as GenericValue[] );
            } )
        ),
        { initialValue: [] as GenericValue[] }
    );

    /** Categories offered by the autocomplete dropdown, narrowed down by the user's query. */
    readonly suggestions = signal<GenericValue[]>( [] );

    private readonly selection: HisSelectFileCategorySelection;

    /**
     * Ids of the categories the user may choose from, mirroring the JSF
     * {@code getAllAvailableDocumentMetadataCategories()}: the categories configured for
     * the upload field plus the categories already carried by the selected files (so that
     * a category assigned earlier stays selectable even if it was removed from the
     * configuration in the meantime).
     */
    readonly availableIds = computed<number[]>( () => {
        const categoryIds = new Set<number>( this.selection.configuration()?.documentMetadataCategoryIds ?? [] );
        for ( const file of this.selection.files() ) {
            if ( file.documentMetadataCategoryId != null ) {
                categoryIds.add( file.documentMetadataCategoryId );
            }
        }

        return [ ...categoryIds ];
    } );

    /**
     * The {@link availableIds} resolved to the corresponding server values, in the order
     * provided by the server. Ids that cannot be resolved are dropped — they still remain
     * assignable through {@link selectedId}, they just cannot be offered as an option.
     */
    readonly available = computed<GenericValue[]>( () => {
        const categoryIds = new Set( this.availableIds() );
        return this.all().filter( ( category ) => categoryIds.has( category.id ) );
    } );

    /**
     * The single category shared by all selected files, mirroring the JSF
     * {@code getCommonDocumentMetadataCategoryId()}. An explicit selection made by the user
     * wins as long as it stays available; otherwise the value is derived from the only
     * available category or from the category all selected files already agree on.
     *
     * Deliberately derived from the raw category ids — and not from the resolved
     * {@link available} categories — so that a category already assigned to a file survives
     * while the server values are still being loaded or cannot be resolved at all.
     */
    readonly selectedId = linkedSignal<{ categoryIds: number[]; files: HisSelectFile[] }, number | null>( {
        source: () => ( {
            categoryIds: this.availableIds(),
            files: this.selection.files(),
        } ),
        computation: ( { categoryIds, files }, previous ) => {
            if ( files.length === 0 ) {
                return null;
            }

            const previousId = previous?.value ?? null;
            if ( previousId != null && categoryIds.includes( previousId ) ) {
                return previousId;
            }

            if ( categoryIds.length === 1 ) {
                return categoryIds[ 0 ];
            }

            const assignedIds = new Set( files.map( ( file ) => file.documentMetadataCategoryId ?? null ) );
            return assignedIds.size === 1 ? assignedIds.values().next().value : null;
        },
    } );

    /** The currently selected category as a server value, used by the dropdown and the readonly display. */
    readonly selected = computed<GenericValue | null>( () => {
        const categoryId = this.selectedId();
        if ( categoryId == null ) {
            return null;
        }

        return this.available().find( ( category ) => category.id === categoryId ) ?? null;
    } );

    /**
     * `true` when the category has to be presented, mirroring the JSF
     * {@code isShowCategorySelection()}: never without a selected file, otherwise whenever
     * the upload field configures more than one category or forces the display of a unique
     * category.
     */
    readonly visible = computed<boolean>( () => {
        if ( this.selection.files().length === 0 ) {
            return false;
        }

        const config = this.selection.configuration();
        return ( config?.showCategoryIfUnique ?? false )
            || ( config?.documentMetadataCategoryIds?.length ?? 0 ) > 1;
    } );

    /**
     * `true` when the category may not be changed — either because the selection is locked
     * as a whole, or because there is nothing to choose from.
     */
    readonly unchangeable = computed<boolean>( () => this.selection.locked() || this.availableIds().length <= 1 );

    /**
     * Category ids offered by the embedded upload component, mirroring the JSF
     * {@code getTmpCategoryIds()}:
     * <ul>
     *   <li>If a common category has been determined, restrict the selection to it.</li>
     *   <li>Otherwise, if any currently selected file already carries a
     *       {@link HisSelectFile.documentMetadataCategoryId}, restrict the
     *       selection to that single category.</li>
     *   <li>Otherwise fall back to the first category from the upload
     *       configuration ({@link HisUploadConfiguration.documentMetadataCategoryIds}),
     *       or none when the configuration has no categories.</li>
     * </ul>
     */
    readonly uploadIds = computed<number[]>( () => {
        const commonCategoryId = this.selectedId();
        if ( commonCategoryId != null ) {
            return [ commonCategoryId ];
        }

        for ( const file of this.selection.files() ) {
            if ( file.documentMetadataCategoryId != null ) {
                return [ file.documentMetadataCategoryId ];
            }
        }

        const categories = this.selection.configuration()?.documentMetadataCategoryIds ?? [];
        return categories.length === 0 ? [] : [ categories[ 0 ] ];
    } );

    /**
     * The category a newly added file starts out with, mirroring the JSF
     * {@code updateSelection()}: the common category — or, while none has been determined
     * yet, the only configured category. Any later change of the common category reaches
     * the file through {@link materialize} anyway.
     */
    readonly forNewFile = computed<number | null>( () => {
        const commonId = this.selectedId();
        if ( commonId != null ) {
            return commonId;
        }

        const categories = this.selection.configuration()?.documentMetadataCategoryIds ?? [];
        return categories.length === 1 ? categories[ 0 ] : null;
    } );

    constructor( selection: HisSelectFileCategorySelection ) {
        this.selection = selection;
    }

    /** Narrows the categories offered by the dropdown down to the user's query. */
    filterSuggestions( event: AutoCompleteCompleteEvent ): void {
        const query = event.query?.toLowerCase() ?? '';

        this.suggestions.set(
            this.available().filter( ( category ) => category.defaulttext?.toLowerCase().includes( query ) )
        );
    }

    /** Applies the category picked by the user to all selected files. */
    choose( category: GenericValue | null ): void {
        this.selectedId.set( category?.id ?? null );
        this.applyToFiles();
    }

    /**
     * Writes a category that has merely been <em>derived</em> back into the selected files.
     *
     * {@link selectedId} determines the common category on its own — from the only available
     * category, or because all selected files already agree on one. Without this step that
     * derived category would never reach the files: the surrounding form would submit them
     * without a category, and {@link validate} would report a {@code categoryMissing} the
     * user cannot even correct, because the category is rendered readonly as soon as there
     * is only a single one to choose from.
     *
     * Nothing is cleared while no category has been determined: unlike an explicit choice
     * made through {@link choose}, the user could not restore a category dropped here.
     *
     * Never call this during change detection: the resulting write flips the validity of
     * the surrounding form, which would raise NG0100 in components that bind to that state,
     * e.g. a submit button's {@code [disabled]}.
     */
    materialize(): void {
        if ( this.selectedId() == null ) {
            return;
        }

        this.applyToFiles();
    }

    /**
     * Mirrors the JSF {@code required="true"} of the category dropdown: as soon as the
     * category has to be presented, every selected file needs to carry one.
     *
     * The files are passed in rather than read from the selection, because the form asks
     * for the state it holds itself — which is not necessarily the state the component has
     * already adopted.
     */
    validate( files: HisSelectFile[] ): ValidationErrors | null {
        if ( !this.visible() ) {
            return null;
        }

        if ( files.every( ( file ) => file.documentMetadataCategoryId != null ) ) {
            return null;
        }

        return {
            categoryMissing: {
                message: this.hisUiService.translate( TRANSLATION_CATEGORY_REQUIRED )
            }
        };
    }

    /**
     * Assigns the common category to every selected file, mirroring the JSF
     * {@code applyProperties()}. The selection is only handed back when at least one file
     * carries a different category, which avoids marking the form dirty without an actual
     * change.
     */
    private applyToFiles(): void {
        const categoryId = this.selectedId();
        const files = this.selection.files();

        if ( files.length === 0 ) {
            return;
        }

        const outdated = files.some( ( file ) => ( file.documentMetadataCategoryId ?? null ) !== categoryId );
        if ( !outdated ) {
            return;
        }

        this.selection.apply( files.map( ( file ) => ( { ...file, documentMetadataCategoryId: categoryId } ) ) );
    }
}
